import type { LoaderFunctionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Form, Link, useLoaderData } from "@remix-run/react";
import { authenticator, DEV_USER } from "~/services/auth.server";
import { TopNav } from "~/components/TopNav";
import { builders } from "~/lib/builders";

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await authenticator.isAuthenticated(request);
  if (user) return json({ user });

  if (process.env.NODE_ENV !== "production") {
    return json({ user: DEV_USER });
  }

  return json({ user: null });
}

const people = builders;

function SlackIcon() {
  return (
    <svg height="12" viewBox="0 0 127 127" width="12">
      <path d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z" fill="#E01E5A"/>
      <path d="M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z" fill="#36C5F0"/>
      <path d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.9 6.5 72.8.6 80.1.6c7.3 0 13.2 5.9 13.2 13.2v33.1z" fill="#2EB67D"/>
      <path d="M80.1 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80.1z" fill="#ECB22E"/>
    </svg>
  );
}

export default function Index() {
  const { user } = useLoaderData<typeof loader>();

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f8fafc",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
      color: "#0f172a",
    }}>
      <TopNav
        userName={user?.displayName}
        userEmail={user?.email}
        userPhotoUrl={user?.photoUrl}
      />

      {/* Sign-in state */}
      {!user && (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "calc(100vh - 56px)",
          padding: "2rem",
        }}>
          <div style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "20px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            padding: "3rem 2.5rem",
            textAlign: "center",
            maxWidth: "380px",
            width: "100%",
          }}>
            <div style={{ marginBottom: "1.25rem" }}>
              <svg
                width="64"
                height="56"
                viewBox="0 0 50 44"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_login_logo)">
                  <path d="M6.92249 12.7175C9.47207 11.6619 11.7163 9.94029 13.4382 7.7193C15.1601 5.49834 16.3012 2.85319 16.7512 0.0397357C16.7512 0.0397357 16.7512 0.0397403 16.7512 0.238452C16.7512 0.437164 16.7512 0.238452 16.7512 0.556391C18.328 4.24632 20.8727 7.40033 24.0942 9.65738C25.0801 10.3054 26.1318 10.8386 27.2301 11.2471C27.6431 11.3656 28.0792 11.3656 28.4922 11.2471C30.4652 10.5238 32.2745 9.38759 33.8082 7.90873C36.0346 5.80738 37.8185 3.24999 39.0476 0.397418C39.0269 0.265816 39.0269 0.131602 39.0476 0C39.5036 2.8235 40.6608 5.47446 42.4047 7.69091C44.1486 9.90741 46.4189 11.6126 48.9911 12.6381C48.188 13.3535 46.1228 13.4329 42.7956 12.956C43.9264 16.6731 45.9385 20.0351 48.6469 22.7326C45.6746 22.96 42.7018 22.298 40.0802 20.825C39.889 20.825 40.0802 21.2224 40.0802 21.4211C40.0802 26.071 40.3479 30.6811 40.3479 35.331C40.3479 36.245 40.1184 36.563 39.3536 36.8412C38.9942 36.966 38.6089 36.9876 38.2387 36.9036C37.8684 36.8197 37.5269 36.6333 37.2501 36.3643C37.1286 36.1468 37.0627 35.9004 37.0589 35.649C37.0589 33.5028 37.0589 31.317 37.0589 29.1709C37.0589 27.0248 37.0589 23.9646 37.0589 21.2224C36.768 21.3088 36.5042 21.4733 36.294 21.6993C33.8467 23.6539 30.8101 24.64 27.7274 24.4813C24.9794 24.3697 22.3497 23.2907 20.2697 21.4211C20.2697 21.4211 20.0403 21.1032 19.849 21.0635C19.6578 21.0237 19.849 21.4211 19.849 21.5801V26.6274C19.849 27.3428 19.849 27.3428 20.5374 27.2632C22.7066 26.9452 24.908 26.9452 27.0771 27.2632C28.2128 27.4206 29.3131 27.7838 30.328 28.3363C31.2681 28.8448 32.0406 29.6336 32.5461 30.6016C33.0152 31.5998 33.1883 32.7203 33.0433 33.8208C32.998 34.1817 32.9342 34.5399 32.8521 34.8939C32.8053 35.0723 32.7412 35.2454 32.6608 35.4105C32.6466 35.2253 32.6466 35.0393 32.6608 34.8541C32.5476 33.9326 32.1598 33.071 31.5518 32.39C30.9475 31.7153 30.1363 31.2798 29.2571 31.158C27.576 30.8698 25.8654 30.8164 24.1706 30.9991C22.7174 30.9991 21.3023 30.9991 19.8873 31.317C19.1606 31.317 19.1989 31.317 19.1606 32.1516C19.1224 32.9862 19.1606 34.4964 19.1606 35.6887C19.217 35.8831 19.217 36.0904 19.1606 36.2848C19.0924 36.4859 18.9736 36.6643 18.8164 36.8014C18.6612 36.945 18.4789 37.0533 18.281 37.1194H17.6691C17.3552 37.0778 17.0471 36.9977 16.7512 36.881C16.5007 36.7787 16.2869 36.5982 16.1393 36.3643C16.0606 36.1192 16.0606 35.8542 16.1393 35.6092C16.1393 30.9196 16.1393 26.23 16.4071 21.5404C16.4071 21.5404 16.4071 21.3019 16.4071 21.2224V20.9045C13.7354 22.4426 10.698 23.1594 7.64916 22.9711C9.1096 21.6268 10.3953 20.0904 11.4736 18.4007C12.5274 16.7564 13.3154 14.9441 13.8065 13.0355C11.5363 13.6808 9.12856 13.5696 6.92249 12.7175ZM24.553 11.6842H24.1324C23.7833 11.6443 23.4301 11.694 23.1039 11.8293C22.7776 11.9646 22.4884 12.1811 22.2617 12.4599C22.035 12.7387 21.8777 13.0711 21.8038 13.4279C21.7299 13.7847 21.7415 14.155 21.8377 14.5059C21.9065 15.0235 22.0349 15.5305 22.2202 16.0162C23.405 18.7003 25.3193 20.9659 27.7274 22.5339C27.9951 22.7326 28.148 22.5339 28.3775 22.5339C30.4465 21.1327 32.164 19.239 33.3874 17.0097C33.838 16.1759 34.124 15.2573 34.2289 14.3073C34.3117 13.8199 34.2268 13.3179 33.989 12.8891C33.7512 12.4604 33.3758 12.1323 32.9286 11.9624C32.6223 11.8429 32.2994 11.7758 31.9724 11.7637C30.741 11.7261 29.5235 12.0424 28.4539 12.6778C28.3122 12.7544 28.1548 12.7944 27.9951 12.7944C27.8353 12.7944 27.6778 12.7544 27.5361 12.6778C26.5899 12.1171 25.5269 11.8035 24.4383 11.7637L24.553 11.6842Z" fill="#4D8C78"/>
                  <path d="M49.6408 34.9733C50.1379 35.6091 50.6734 37.9937 47.1167 40.6961C45.1491 42.1755 42.8888 43.1802 40.5004 43.6371C37.66 44.2015 34.7346 44.1066 31.9338 43.3589C30.371 43.0048 29.0006 42.0364 28.1093 40.6564C27.8364 40.2237 27.654 39.7362 27.5739 39.2257C27.4586 38.7294 27.4586 38.2117 27.5739 37.7155C27.8899 36.5986 28.4383 35.5674 29.1802 34.6951C29.1802 35.3309 29.1802 35.8078 29.1802 36.3245C29.2029 37.3388 29.5556 38.3151 30.1806 39.0945C30.8056 39.8739 31.6662 40.4104 32.6221 40.6166C33.7105 40.9943 34.8421 41.2214 35.9876 41.2923C38.1435 41.5008 40.3176 41.2564 42.3807 40.5736C44.4438 39.8908 46.3535 38.7837 47.9963 37.318C49.9467 35.7284 48.9906 34.9733 49.6408 34.9733Z" fill="#4D8C78"/>
                  <path d="M0 35.5694C2.06531 33.9204 4.34418 32.5825 6.76922 31.5952C8.56672 30.8401 10.5937 30.1644 12.2381 29.5684C12.4252 29.5098 12.6247 29.5098 12.8118 29.5684C13.0102 29.612 13.1946 29.7077 13.3472 29.8466C13.6447 30.0504 13.8932 30.3222 14.0739 30.6413C14.2631 30.9575 14.3929 31.3082 14.4563 31.6746C14.4563 32.2311 14.4563 32.3106 13.8826 32.3901C10.226 32.9448 6.61913 33.8088 3.09778 34.9733L1.22381 35.6887C0.803134 35.8874 0.344198 35.9669 0 35.5694Z" fill="#4D8C78"/>
                </g>
                <defs>
                  <clipPath id="clip0_login_logo">
                    <rect width="50" height="44" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </div>
            <h1 style={{
              color: "#0f172a",
              fontSize: "1.375rem",
              fontWeight: 700,
              margin: "0 0 0.5rem 0",
            }}>
              Pine Park Health Grove
            </h1>
            <p style={{
              color: "#64748b",
              fontSize: "0.9375rem",
              margin: "0 0 2rem 0",
              lineHeight: "1.5",
            }}>
              Sign in to access the prototyping environment
            </p>
            <Form action="/auth/google" method="post">
              {/* Official Google Sign-In button per branding guidelines */}
              <button
                type="submit"
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 1px 3px 0 rgba(60,64,67,0.30), 0 4px 8px 3px rgba(60,64,67,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 1px 2px 0 rgba(60,64,67,0.30), 0 1px 3px 1px rgba(60,64,67,0.15)";
                }}
                style={{
                  background: "white",
                  border: "none",
                  borderRadius: "4px",
                  boxShadow: "0 1px 2px 0 rgba(60,64,67,0.30), 0 1px 3px 1px rgba(60,64,67,0.15)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "stretch",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  fontWeight: 500,
                  height: "40px",
                  padding: 0,
                  transition: "box-shadow 0.2s",
                  width: "100%",
                }}
              >
                {/* Icon container */}
                <div style={{
                  alignItems: "center",
                  background: "white",
                  borderRadius: "4px 0 0 4px",
                  display: "flex",
                  flexShrink: 0,
                  justifyContent: "center",
                  padding: "0 10px",
                }}>
                  <svg height="18" viewBox="0 0 48 48" width="18" xmlns="http://www.w3.org/2000/svg">
                    <path d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z" fill="#FFC107"/>
                    <path d="M6.3 14.7l7 5.1C15.2 16.4 19.3 14 24 14c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 16.3 2 9.6 7.3 6.3 14.7z" fill="#FF3D00"/>
                    <path d="M24 46c5.5 0 10.5-1.9 14.3-5.1l-6.6-5.6C29.7 37.1 27 38 24 38c-6.1 0-10.7-3.9-11.8-9.1L5 34.3C8.2 41.2 15.5 46 24 46z" fill="#4CAF50"/>
                    <path d="M44.5 20H24v8.5h11.8c-1 2.9-3 5.3-5.7 6.9l6.6 5.6C41.3 37.4 44.5 31.2 44.5 24c0-1.3-.2-2.7-.5-4z" fill="#1976D2"/>
                  </svg>
                </div>
                {/* Text container */}
                <div style={{
                  alignItems: "center",
                  color: "rgba(0,0,0,0.54)",
                  display: "flex",
                  flex: 1,
                  justifyContent: "center",
                  letterSpacing: "0.21px",
                  paddingRight: "10px",
                }}>
                  Sign in with Google
                </div>
              </button>
            </Form>
          </div>
        </div>
      )}

      {/* Logged-in state */}
      {user && (
        <main style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "4rem 2rem 5rem",
        }}>

          {/* Hero */}
          <div style={{ marginBottom: "3.5rem" }}>
            <div style={{ marginBottom: "1rem" }}>
              <span style={{
                background: "rgba(99, 102, 241, 0.08)",
                border: "1px solid rgba(99, 102, 241, 0.2)",
                borderRadius: "9999px",
                color: "#6366f1",
                fontSize: "0.6875rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                padding: "0.3rem 0.875rem",
                textTransform: "uppercase",
              }}>
                Grove Environment
              </span>
            </div>
            <h1 style={{
              color: "#0f172a",
              fontSize: "3rem",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              margin: "0 0 0.75rem 0",
              lineHeight: 1.1,
            }}>
              Pine Park Health Grove
            </h1>
            <p style={{
              color: "#64748b",
              fontSize: "1.125rem",
              margin: "0 0 1.5rem 0",
              lineHeight: 1.6,
            }}>
              Build, iterate, and share prototypes with test data — safely.
            </p>
            <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
              {[
                { icon: "📸", label: "Test patient data" },
                { icon: "🔒", label: "Production-level auth" },
                { icon: "☁️", label: "Google Cloud VPN" },
              ].map(({ icon, label }) => (
                <div key={label} style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  color: "#94a3b8",
                  fontSize: "0.875rem",
                }}>
                  <span>{icon}</span>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tutorial Feature Card */}
          <Link to="/tutorial" style={{ textDecoration: "none", display: "block", marginBottom: "3.5rem" }}>
            <div
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(99, 102, 241, 0.5)";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 32px rgba(99, 102, 241, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(99, 102, 241, 0.2)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 12px rgba(99, 102, 241, 0.06)";
              }}
              style={{
                background: "linear-gradient(135deg, #eef2ff 0%, #f5f3ff 60%, #faf5ff 100%)",
                border: "1px solid rgba(99, 102, 241, 0.2)",
                borderRadius: "16px",
                padding: "2.25rem 2.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "2rem",
                transition: "all 0.2s",
                boxShadow: "0 2px 12px rgba(99, 102, 241, 0.06)",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{
                  color: "#6366f1",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  marginBottom: "0.75rem",
                }}>
                  New to Grove? Start here
                </div>
                <h2 style={{
                  color: "#1e1b4b",
                  fontSize: "1.875rem",
                  fontWeight: 700,
                  margin: "0 0 0.75rem 0",
                  letterSpacing: "-0.02em",
                }}>
                  Prototype Tutorial
                </h2>
                <p style={{
                  color: "#4c4f7a",
                  fontSize: "1rem",
                  margin: "0 0 1.5rem 0",
                  maxWidth: "520px",
                  lineHeight: 1.65,
                }}>
                  Learn how to build click-through prototypes with test patient data, iterate quickly, and share with your team for feedback.
                </p>
                <div style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  background: "#6366f1",
                  borderRadius: "8px",
                  color: "#ffffff",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  padding: "0.5rem 1.125rem",
                }}>
                  Open Tutorial →
                </div>
              </div>
              <div style={{
                fontSize: "5.5rem",
                flexShrink: 0,
                lineHeight: 1,
                opacity: 0.8,
                userSelect: "none",
              }}>
                📚
              </div>
            </div>
          </Link>

          {/* Builder Spaces */}
          <div>
            <h2 style={{
              color: "#94a3b8",
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: "0.08em",
              margin: "0 0 1.25rem 0",
              textTransform: "uppercase",
            }}>
              Builder Spaces
            </h2>
            <div style={{
              display: "grid",
              gap: "1rem",
              gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
            }}>
              {people.map((person) => (
                <Link
                  key={person.slug}
                  to={`/builders/${person.slug}`}
                  style={{ textDecoration: "none" }}
                >
                  <div
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#c7d2fe";
                      e.currentTarget.style.transform = "translateY(-3px)";
                      e.currentTarget.style.boxShadow = "0 8px 24px rgba(99, 102, 241, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e2e8f0";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)";
                    }}
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "14px",
                      padding: "1.75rem 1rem 1.5rem",
                      textAlign: "center",
                      transition: "all 0.2s",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    {/* Avatar with Slack badge */}
                    <div style={{
                      position: "relative",
                      display: "inline-block",
                      marginBottom: "0.875rem",
                    }}>
                      <img
                        alt={person.name}
                        src={person.imageUrl}
                        style={{
                          borderRadius: "50%",
                          height: "60px",
                          objectFit: "cover",
                          width: "60px",
                          display: "block",
                        }}
                      />
                      <a
                        href={person.slackUrl}
                        onClick={(e) => e.stopPropagation()}
                        rel="noopener noreferrer"
                        target="_blank"
                        title={`Message ${person.name} on Slack`}
                        style={{
                          position: "absolute",
                          bottom: -1,
                          right: -1,
                          background: "#ffffff",
                          borderRadius: "50%",
                          border: "1.5px solid #e2e8f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "3px",
                          lineHeight: 0,
                        }}
                      >
                        <SlackIcon />
                      </a>
                    </div>

                    {/* Name */}
                    <div style={{
                      color: "#0f172a",
                      fontWeight: 600,
                      fontSize: "1rem",
                      marginBottom: "0.25rem",
                    }}>
                      {person.name}
                    </div>
                    <div style={{
                      color: "#94a3b8",
                      fontSize: "0.75rem",
                    }}>
                      View space →
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </main>
      )}

      <footer style={{
        textAlign: "center",
        padding: "2rem",
        color: "#cbd5e1",
        fontSize: "0.75rem",
        borderTop: "1px solid #e2e8f0",
      }}>
        Pine Park Grove • Experimental Environment • {new Date().getFullYear()}
      </footer>
    </div>
  );
}
